import React from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as BannerFlower } from '../../forum/assets/images/banner-flower.svg';
import CustomButton from '../../../app/components/button/CustomButton';
import styled from 'styled-components';
import bgFlower from '../../forum/assets/images/bg-flower.svg';
import HeroImage from '../assets/Images/HeroImage.webp';
import SeoHead from '../../../app/components/SeoHead';

const ForumBanner = styled.div`
  padding: 100px 0 0px;
  position: relative;
  background-color: ${({ theme }) => theme.white};
  &::after {
    height: 226px;
    width: 157px;
    background-repeat: no-repeat;
    background-position: right;
    content: '';
    right: 0;
    bottom: 126px;
    position: absolute;
    background-image: url(${bgFlower});
    @media (max-width: 575px) {
      display: none;
    }
  }
`;

const BannerInfo = styled.div`
  max-width: 550px;
  h1 {
    line-height: 1.36;
    font-size: 52px;
    font-family: 'Source Serif 4', serif;
    color: ${({ theme }) => theme.secondaryLight};
    position: relative;
    margin-bottom: 32px;
    font-weight: 400;
    &::after {
      position: absolute;
      height: 27px;
      max-width: 350px;
      width: 100%;
      bottom: 4px;
      left: 0;
      background: #7d3dae1a;
      content: '';
    }
  }
  p {
    color: ${({ theme }) => theme.secondaryLight};
    font-size: 18px;
    font-weight: 400;
    margin-bottom: 32px;
  }
`;

const HighLightText = styled.span`
  background-color: ${({ theme }) => theme.badgeText}1a;
  padding: 8px 16px;
  font-weight: 400;
  display: inline-flex;
  font-size: 18px;
  align-items: center;
  color: ${({ theme }) => theme.badgeText};
  border-radius: 50px;
  margin-bottom: 25px;
  svg {
    margin-right: 10px;
  }
`;

const BannerButton = styled(CustomButton)`
  background-color: ${({ theme }) => theme.mainPrimary};
  font-size: 20px;
  padding: 15px 30px;
  font-weight: 400;
  border-radius: 50px;
  margin-bottom: 30px;
  border-color: ${({ theme }) => theme.mainPrimary};
  &:hover,
  &:focus,
  &:active {
    border-color: ${({ theme }) => theme.mainPrimary} !important;
    background-color: ${({ theme }) => theme.mainPrimary} !important;
  }
`;

const CounterBanner = styled.div`
  margin-top: -40px;
  border-bottom: 36px solid ${({ theme }) => theme.mainPrimary}0d;
  position: relative;
  font-weight: 400;
  @media (max-width: 575px) {
    margin-top: 0px;
    background-color: ${({ theme }) => theme.mainPrimary}0d;
    border: 0;
  }
  &::after {
    border-top-right-radius: 25px;
    top: 0;
    left: -10%;
    position: absolute;
    width: 51%;
    height: 100%;
    z-index: 0;
    content: '';
    background-color: ${({ theme }) => theme.mainPrimary};
    opacity: 0.05;
    transform: skew(35deg, 0deg);
    @media (max-width: 575px) {
      display: none;
    }
  }
  &::before {
    border-radius: 0 0 0 15px;
    bottom: -0.3px;
    left: 42.4%;
    position: absolute;
    width: 30px;
    height: 50px;
    z-index: 1;
    content: '';
    background-color: ${({ theme }) => theme.white};
    transform: skew(30deg, 0deg);
    @media only screen and (max-width: 1600px) {
      left: 42.7%;
    }
    @media (max-width: 575px) {
      display: none;
    }
  }
`;

const Counter = styled.ul`
  padding: 36px 0 0 0;
  margin: 0;
  list-style: none;
  z-index: 1;
  position: relative;
  @media (max-width: 575px) {
    padding: 15px 0;
  }
  li {
    padding-right: 96px;
    position: relative;
    &:last-child {
      padding: 0;
      &::after {
        display: none;
      }
    }
    &::after {
      top: 50%;
      transform: translateY(-50%);
      content: '';
      height: 66px;
      width: 1px;
      right: 48px;
      background-color: ${({ theme }) => theme.mainPrimary};
      position: absolute;
    }
    h5 {
      font-size: 48px;
      color: ${({ theme }) => theme.secondaryLight};
      line-height: 1.36;
      font-family: 'Source Serif 4', serif;
      font-weight: 400;
      margin-bottom: 0px;
      text-transform: uppercase;
    }
    span {
      color: ${({ theme }) => theme.secondaryLight};
      font-size: 20px;
      opacity: 0.8;
    }
  }
`;

const ParentContentDiv = styled.div`
  margin-bottom: 100px;
`;

const HeroImageDiv = styled.img`
  @media (max-width: 575px) {
    max-width: 360px;
    height: auto;
  }
`;

export default function Banner() {
  const navigate = useNavigate();

  const handleRedirectToLogin = () => {
    navigate('/login');
  };

  const seoData = {
    title: 'Florists',
    description:
      'Building and Hosting your e-commerce Floral Website has never been easier or quicker.  The BoostFloral platform addresses the specific needs of Florists.',
    keywords:
      'Flower Shop Websites, Floral Web Design Software, Floral CMS, Building a Floral Website, Floral POS, Floral point of sale, Floral website builder, Build a Floral Website, Florist website design, Floral website design, best floral website templates, Florist web templates, Create a flower shop website, Floral e-commerce website, Best website platform for florists, Flower shop online store, Florist online ordering system, Flower delivery website, POS system for florists, Florist website SEO, Customizable florist website, Floral event booking website, the best website for retail flower shops',
    ogTitle: 'Florists',
    ogDescription:
      'Building and Hosting your e-commerce Floral Website has never been easier or quicker with the Number 1 Floral Web Design Software.  The BoostFloral software addresses the specific needs of Florists.',
    ogType: 'company',
    ogSiteName: 'BoostFloral',
    ogImage: 'https://www.boostfloral.com/logo.svg',
  };

  return (
    <ForumBanner>
      <SeoHead data={seoData} />
      <Container>
        <ParentContentDiv>
          <Row className="align-items-center">
            <Col md={6}>
              <BannerInfo>
                <HighLightText>
                  {' '}
                  <BannerFlower /> Share in a centralized florist directory
                </HighLightText>
                <h1>Quickly and Easily locate fellow Florists</h1>
                <p>
                  A map designed for Florists to share their service info with
                  each other, and locate places of interest
                </p>
                <BannerButton onClick={handleRedirectToLogin}>
                  Free to Participate
                </BannerButton>
              </BannerInfo>
            </Col>
            <Col md={6}>
              <div className="text-end">
                <HeroImageDiv src={HeroImage} alt="Flower delivery website" />
              </div>
            </Col>
          </Row>
        </ParentContentDiv>
      </Container>
      <CounterBanner>
        <Container>
          <Counter className="d-flex align-items-center">
            <li>
              <h5>2k+</h5>
              <span>World Class Florist</span>
            </li>
            <li>
              <h5>500+</h5>
              <span>Daily Idea Sharing</span>
            </li>
          </Counter>
        </Container>
      </CounterBanner>
    </ForumBanner>
  );
}
